import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Alert, App as AntApp, Checkbox, Col, Divider, Form, Modal, Row, Tree } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAdminAddAgentUserAccount,
  useEnumeration,
  useGetAgentEnumerations,
  useGetPermissionDefinitions,
} from "../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import { AntTreeStrictCheckedProps } from "../../../../common/types";
import { UUID } from "../../../../typings/global";
import { AgentType } from "../../../agent/enums";
import { useIsSystemAdmin } from "../../../auth/hooks";
import { userChanged } from "../../../auth/session";
import { AgentSelect } from "../../../enumerations/components/form/AgentSelect";
import { EnumerationType } from "../../../enumerations/enums";
import { AdminCreateUpdateAgentUserAccount, UserAdminView } from "../../types";
import {
  buildPermissionTreeNodes,
  filterPermissionsWithPrerequisites,
  hasConfirmedAgentUserAccount,
} from "../../utils";
import { DefaultPermissionsFormPart } from "./DefaultPermissionsFormPart";

interface Props {
  open: boolean;
  user: UserAdminView;
  onFormCancel: () => void;
}

const colSpan = 8;

export const AdminCreateUserAccountForm = ({ open, user, onFormCancel }: Props) => {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();

  const isCurrentUserSystemAdmin = useIsSystemAdmin();
  const topAgentAllowedFeatures = useEnumeration(EnumerationType.TOP_AGENT_ALLOWED_FEATURES);

  const { data: permissionDefinitions } = useGetPermissionDefinitions();

  const [form] = Form.useForm<AdminCreateUpdateAgentUserAccount>();

  const adminAddAgentUserAccount = useAdminAddAgentUserAccount();

  const [selectedAgentId, setSelectedAgentId] = useState<UUID>();
  const [formCheckedPermissions, setFormCheckedPermissions] = useState<Permission[]>([]);

  const { data: selectedAgentsData } = useGetAgentEnumerations(selectedAgentId ? [selectedAgentId] : []);
  const selectedAgent = selectedAgentsData?.[0];

  const handleFormCancel = () => {
    onFormCancel();
    setFormCheckedPermissions([]);
  };

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (formCheckedPermissions.length === 0) {
          message.error(t("user.helpers.noPermissions"));
        } else {
          adminAddAgentUserAccount.mutate(
            { id: user.id, object: { ...values, permissions: formCheckedPermissions } },
            {
              onSuccess: () => {
                userChanged(user.id);
                handleFormCancel();
              },
              onError: (error) => setErrorsToForm(form, "user.attrs", error.violations),
            },
          );
        }
      })
      .catch(resolveFormValidationError);
  };

  const handlePermissionTreeCheck = (checked: AntTreeStrictCheckedProps) => {
    const checkedPermissions = checked.checked.map((checked) => Permission[checked as keyof typeof Permission]);
    setFormCheckedPermissions(filterPermissionsWithPrerequisites(permissionDefinitions, checkedPermissions));
  };

  const handleAgentChange = (agentId: UUID) => {
    form.setFieldsValue({ representingAgentId: undefined });
    setSelectedAgentId(agentId);
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={t("user.titles.addAccount")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={adminAddAgentUserAccount.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={handleFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="agentUserAccountCreateForm">
        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item name="agentId" label={t("user.attrs.agentId")} rules={[validations.notNull]}>
              <AgentSelect onChange={handleAgentChange} />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="representingAgentId"
              label={
                <LabelWithTooltip
                  label={t("user.attrs.representingAgentId")}
                  tooltip={t("user.helpers.representingAgentInfo")}
                />
              }
              rules={selectedAgent?.type === AgentType.LEGAL ? [validations.notNull] : [validations.none]}
            >
              <AgentSelect
                optionsProps={{
                  allowedIds: selectedAgent?.representatives?.map((representative) => representative.id),
                  forceRender: true,
                }}
                disabled={selectedAgent?.type !== AgentType.LEGAL}
                allowClear
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="disabled"
              className="form-item-without-label"
              valuePropName="checked"
              initialValue={false}
              rules={[validations.none]}
            >
              <Checkbox>{t("user.attrs.disabled")}</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Divider titlePlacement="start" className="divider-subheader">
          {t("user.titles.permissionsSettings")}
        </Divider>

        <DefaultPermissionsFormPart
          allowedFeatures={topAgentAllowedFeatures}
          onPermissionsSet={setFormCheckedPermissions}
        />

        <Row gutter={rowGutter}>
          <Col span={colSpan * 3}>
            <Form.Item label={t("user.attrs.permissionsLabel")} required>
              <Tree
                showLine={{ showLeafIcon: false }}
                checkStrictly
                checkable
                checkedKeys={{ checked: formCheckedPermissions, halfChecked: [] }}
                treeData={buildPermissionTreeNodes(
                  permissionDefinitions,
                  topAgentAllowedFeatures,
                  formCheckedPermissions,
                  isCurrentUserSystemAdmin,
                  user?.agentUserRole?.systemAdmin,
                )}
                onCheck={(data) => handlePermissionTreeCheck(data as AntTreeStrictCheckedProps)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {user && hasConfirmedAgentUserAccount(user) && (
        <Alert title={t("user.helpers.updateInfo")} type="info" showIcon />
      )}
    </Modal>
  );
};
